import { useEffect, useId, useRef, useState } from 'react'
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react'
import { ChevronDown, Eye, EyeOff } from 'lucide-react'

import { cn } from '@/lib/cn'

const CONTROL_BASE = cn(
  // Wypełnione tło zamiast twardej ramki — zgodnie z DESIGN.md.
  //
  // `text-base` na urządzeniach dotykowych, `text-sm` dalej na pulpicie.
  // Safari na iOS samo przybliża stronę po dotknięciu pola o czcionce mniejszej
  // niż 16 px i nie da się tego wyłączyć znacznikiem viewport — jedynym
  // lekarstwem jest odpowiednio duży tekst w kontrolce.
  'w-full rounded-lg bg-surface-container-low px-4 py-2.5 text-base text-on-surface sm:text-sm',
  'border-2 border-transparent transition-colors',
  'placeholder:text-on-surface-variant/50',
  'focus:border-primary focus:bg-surface focus:outline-none focus:ring-0',
  'disabled:cursor-not-allowed disabled:opacity-60',
)

interface FieldWrapperProps {
  label: string
  htmlFor: string
  error?: string
  hint?: string
  required?: boolean
  children: ReactNode
}

function FieldWrapper({ label, htmlFor, error, hint, required, children }: FieldWrapperProps) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-semibold text-on-surface">
        {label}
        {required && (
          <span className="ml-1 text-primary" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      {error ? (
        <p className="text-xs font-semibold text-error" role="alert">
          {error}
        </p>
      ) : (
        hint && <p className="text-xs text-on-surface-variant">{hint}</p>
      )}
    </div>
  )
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string
  error?: string
  hint?: string
  leadingIcon?: ReactNode
}

export function TextField({
  label,
  error,
  hint,
  leadingIcon,
  className,
  required,
  type = 'text',
  ...props
}: TextFieldProps) {
  const id = useId()
  const describedBy = error ? `${id}-error` : undefined

  // Pola hasła dostają przycisk podglądu. Literówka w haśle jest niewidoczna,
  // a przy wymuszonej zmianie hasła użytkownik wpisuje je trzy razy z rzędu.
  const isPassword = type === 'password'
  const [isRevealed, setIsRevealed] = useState(false)
  const effectiveType = isPassword && isRevealed ? 'text' : type

  return (
    <FieldWrapper label={label} htmlFor={id} error={error} hint={hint} required={required}>
      <div className="relative">
        {leadingIcon && (
          <span
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant"
            aria-hidden="true"
          >
            {leadingIcon}
          </span>
        )}
        <input
          id={id}
          type={effectiveType}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
          aria-required={required}
          className={cn(
            CONTROL_BASE,
            leadingIcon ? 'pl-10' : null,
            isPassword ? 'pr-12' : null,
            error && 'border-error',
            className,
          )}
          {...props}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setIsRevealed((current) => !current)}
            aria-label={isRevealed ? 'Ukryj hasło' : 'Pokaż hasło'}
            aria-pressed={isRevealed}
            title={isRevealed ? 'Ukryj hasło' : 'Pokaż hasło'}
            // tabIndex -1: Tab ma prowadzić z pola hasła wprost do przycisku
            // logowania, a nie zatrzymywać się na podglądzie.
            tabIndex={-1}
            className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-on-surface-variant transition-colors hover:bg-surface-container hover:text-primary"
          >
            {isRevealed ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
    </FieldWrapper>
  )
}

interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string
  error?: string
  hint?: string
  options: { value: string; label: string }[]
  placeholder?: string
}

export function SelectField({
  label,
  error,
  hint,
  options,
  placeholder,
  className,
  required,
  ...props
}: SelectFieldProps) {
  const id = useId()

  return (
    <FieldWrapper label={label} htmlFor={id} error={error} hint={hint} required={required}>
      <select
        id={id}
        aria-invalid={Boolean(error)}
        className={cn(CONTROL_BASE, 'cursor-pointer pr-10', error && 'border-error', className)}
        {...props}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldWrapper>
  )
}

interface MultiSelectProps {
  label: string
  error?: string
  hint?: string
  options: { value: string; label: string }[]
  selected: string[]
  onChange: (values: string[]) => void
  disabled?: boolean
  required?: boolean
  /** Tekst na zwiniętym polu, gdy nic nie wybrano. */
  placeholder?: string
  /** Co pokazać, gdy nie ma z czego wybierać. */
  emptyMessage?: string
}

/**
 * Wielokrotny wybór schowany w rozwijanym polu.
 *
 * Wcześniej te same dane były rzędem zaznaczanych plakietek. Przy kilkunastu
 * instrumentach rozpychało to formularz na kilka wierszy i spychało resztę pól
 * poza ekran telefonu — a wybór dotyczy zwykle jednej, dwóch pozycji. Zwinięte
 * pole zajmuje tyle samo miejsca co każde inne i pokazuje wybór w jednej linii.
 */
/** Docelowa wysokość rozwiniętej listy, gdy jest na nią miejsce. */
const WYSOKOSC_LISTY = 240

/** Poniżej tylu pikseli lista w dół robi się bezużyteczna i wolimy ją odwrócić. */
const CIASNO = 176

const ODSTEP = 8

/**
 * Krawędzie obszaru, w którym lista ma się zmieścić.
 *
 * Zwykle są nimi krawędzie okna przeglądarki, ale pole często stoi w oknie
 * modalnym z własnym przewijaniem — wtedy liczą się krawędzie tego okna, bo
 * poza nie lista i tak zostanie przycięta.
 */
function obszarWidoczny(element: HTMLElement): { top: number; bottom: number } {
  for (let rodzic = element.parentElement; rodzic; rodzic = rodzic.parentElement) {
    const { overflowY } = getComputedStyle(rodzic)
    if (overflowY === 'auto' || overflowY === 'scroll') {
      const prostokat = rodzic.getBoundingClientRect()
      return {
        top: Math.max(prostokat.top, 0),
        bottom: Math.min(prostokat.bottom, window.innerHeight),
      }
    }
  }
  return { top: 0, bottom: window.innerHeight }
}

/**
 * Gdzie i jak wysoko rozwinąć listę, żeby zmieściła się w całości.
 *
 * Domyślnie w dół — inaczej lista zasłaniałaby etykietę własnego pola. W górę
 * dopiero wtedy, gdy pod polem zostało tak mało miejsca, że widać by było
 * ledwie parę pozycji, a nad polem jest go więcej.
 */
function ustawienieListy(pole: HTMLElement): { doGory: boolean; wysokosc: number } {
  const { top, bottom } = pole.getBoundingClientRect()
  const obszar = obszarWidoczny(pole)
  const podSpodem = obszar.bottom - bottom - ODSTEP
  const nadPolem = top - obszar.top - ODSTEP

  const doGory = podSpodem < CIASNO && nadPolem > podSpodem
  // Dolna granica na wypadek pola wciśniętego w naprawdę niski obszar: lepiej
  // pozwolić liście wyjść poza niego, niż pokazać pasek wysokości dwóch pikseli.
  const wysokosc = Math.max(120, Math.min(WYSOKOSC_LISTY, doGory ? nadPolem : podSpodem))
  return { doGory, wysokosc }
}

/** Ile nazw mieści się na zwiniętym polu, zanim reszta zamieni się w licznik. */
const WIDOCZNE_NAZWY = 2

/**
 * Podpis zwiniętego pola: kilka nazw, reszta jako licznik.
 *
 * Wypisanie wszystkiego nie mieściło się w polu i kończyło uciętym w połowie
 * wyrazem, z którego nic nie wynikało. Stała liczba nazw zamiast mierzenia
 * szerokości tekstu: podpis nie zmienia się przy zmianie rozmiaru okna, więc
 * nie miga przy przechodzeniu na telefon.
 *
 * Formy „inny/inne” pasują do rodzaju męskonieżywotnego (instrument, oddział) —
 * czyli do wszystkich obecnych zastosowań. Rodzaj żeński („sale”) wymagałby
 * osobnego tekstu podanego z zewnątrz.
 */
function podsumujWybor(nazwy: string[]): string {
  if (nazwy.length <= WIDOCZNE_NAZWY) return nazwy.join(', ')

  const pozostale = nazwy.length - WIDOCZNE_NAZWY
  const licznik = pozostale === 1 ? '+1 inny' : `+${pozostale} inne`
  return `${nazwy.slice(0, WIDOCZNE_NAZWY).join(', ')} ${licznik}`
}

export function MultiSelect({
  label,
  error,
  hint,
  options,
  selected,
  onChange,
  disabled,
  required,
  placeholder = 'Nie wybrano',
  emptyMessage = 'Brak pozycji do wyboru.',
}: MultiSelectProps) {
  const id = useId()
  const [isOpen, setIsOpen] = useState(false)
  const [rozwiniecie, setRozwiniecie] = useState({ doGory: false, wysokosc: WYSOKOSC_LISTY })
  const wrapperRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return

    // Kliknięcie gdziekolwiek indziej zamyka listę. `mousedown`, nie `click`:
    // inaczej wciśnięcie przycisku poza polem zamykałoby listę dopiero po tym,
    // jak ten przycisk zdążył zadziałać na zmienionym układzie strony.
    const zamknijPozaPolem = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setIsOpen(false)
    }
    const zamknijEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', zamknijPozaPolem)
    // Faza przechwytywania: pole bywa w oknie modalnym, które też słucha
    // Escape. Bez tego jedno naciśnięcie zamykałoby cały formularz.
    document.addEventListener('keydown', zamknijEscape, true)

    // Rozwinięta lista potrafi wyjść poza widoczną część przewijanego
    // formularza — przy dolnych polach użytkownik zobaczyłby sam jej skrawek.
    listRef.current?.scrollIntoView({ block: 'nearest' })

    return () => {
      document.removeEventListener('mousedown', zamknijPozaPolem)
      document.removeEventListener('keydown', zamknijEscape, true)
    }
  }, [isOpen])

  const toggle = (value: string) => {
    onChange(
      selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value],
    )
  }

  // Kolejność z listy, nie z zaznaczania — inaczej podsumowanie przestawiałoby
  // się przy każdym kliknięciu.
  const wybrane = options.filter((option) => selected.includes(option.value))
  const podsumowanie = podsumujWybor(wybrane.map((option) => option.label))

  return (
    <FieldWrapper label={label} htmlFor={id} error={error} hint={hint} required={required}>
      <div className="relative" ref={wrapperRef}>
        <button
          id={id}
          type="button"
          disabled={disabled || options.length === 0}
          onClick={() => {
            // Mierzymy w chwili otwierania, a nie przy każdym renderze:
            // rozwinięta lista sama zmienia układ strony, więc ponowny pomiar
            // potrafiłby ją przerzucać w kółko.
            const pole = wrapperRef.current
            if (!isOpen && pole) setRozwiniecie(ustawienieListy(pole))
            setIsOpen((current) => !current)
          }}
          aria-expanded={isOpen}
          aria-invalid={Boolean(error)}
          className={cn(
            CONTROL_BASE,
            // `min-w-0` na przycisku i na podpisie: bez tego element zginający
            // się (`flex`) nie schodzi poniżej szerokości swojej treści, więc
            // długi podpis rozpychał pole poza formularz zamiast się przyciąć.
            'flex min-w-0 cursor-pointer items-center justify-between gap-2 text-left',
            error && 'border-error',
          )}
        >
          <span
            className={cn('min-w-0 truncate', wybrane.length === 0 && 'text-on-surface-variant')}
          >
            {options.length === 0 ? emptyMessage : podsumowanie || placeholder}
          </span>
          <ChevronDown
            size={18}
            aria-hidden="true"
            className={cn(
              'shrink-0 text-on-surface-variant transition-transform',
              isOpen && 'rotate-180',
            )}
          />
        </button>

        {isOpen && (
          <div
            ref={listRef}
            role="group"
            aria-label={label}
            style={{ maxHeight: rozwiniecie.wysokosc }}
            className={cn(
              'absolute left-0 right-0 z-20 overflow-y-auto rounded-lg border border-outline/15 bg-surface p-1 shadow-ambient',
              rozwiniecie.doGory ? 'bottom-full mb-1' : 'top-full mt-1',
            )}
          >
            {options.map((option) => {
              const checked = selected.includes(option.value)
              return (
                <label
                  key={option.value}
                  className={cn(
                    'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                    checked
                      ? 'bg-primary/10 font-semibold text-primary'
                      : 'text-on-surface hover:bg-surface-container-low',
                  )}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-outline/40 text-primary focus:ring-primary"
                    checked={checked}
                    onChange={() => toggle(option.value)}
                  />
                  {option.label}
                </label>
              )
            })}
          </div>
        )}
      </div>
    </FieldWrapper>
  )
}
